import { useEffect, useRef, useCallback } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { ListNode, ListItemNode } from "@lexical/list";
import { LinkNode, AutoLinkNode } from "@lexical/link";
import { $generateHtmlFromNodes } from "@lexical/html";
import {
  $getRoot,
  $createParagraphNode,
  $createTextNode,
  $insertNodes,
  CLEAR_HISTORY_COMMAND,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  type EditorState,
  type LexicalEditor,
} from "lexical";
import { $createHeadingNode, $createQuoteNode } from "@lexical/rich-text";
import { $createListItemNode, $createListNode } from "@lexical/list";
import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from "@lexical/list";
import { TOGGLE_LINK_COMMAND } from "@lexical/link";

interface LexicalEditorWrapperProps {
  initialHtml?: string;
  onChange: (html: string) => void;
  onEditorReady?: (editor: LexicalEditor) => void;
}

const theme = {
  paragraph: "mb-4 leading-relaxed",
  heading: {
    h1: "font-heading text-2xl font-medium text-foreground-950 mt-6 mb-3",
    h2: "font-heading text-xl font-medium text-foreground-950 mt-5 mb-2",
    h3: "font-heading text-lg font-medium text-foreground-900 mt-4 mb-2",
  },
  list: {
    ul: "list-disc pl-6 mb-4 space-y-1",
    ol: "list-decimal pl-6 mb-4 space-y-1",
    listitem: "mb-1 leading-relaxed",
  },
  quote: "border-l-3 border-primary-300 pl-4 my-4 italic text-foreground-600",
  link: "text-primary-600 underline underline-offset-2 hover:text-primary-700",
  text: {
    bold: "font-semibold",
    italic: "italic",
    underline: "underline underline-offset-2",
  },
};

/* ── Toolbar (standalone, no ContentEditable) ── */
function ToolbarPlugin({
  editorRef,
  onPasteWord,
}: {
  editorRef: React.MutableRefObject<LexicalEditor | null>;
  onPasteWord: () => void;
}) {
  const [editor] = useLexicalComposerContext();
  const linkUrlRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    editorRef.current = editor;
  }, [editor, editorRef]);

  const formatBold = () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
  const formatItalic = () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
  const formatUnderline = () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline");

  const formatHeading = (level: "h2" | "h3") => {
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, level);
  };

  const formatBulletList = () => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
  const formatOrderedList = () => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);

  const formatLink = () => {
    const url = linkUrlRef.current?.value;
    if (url) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
      if (linkUrlRef.current) linkUrlRef.current.value = "";
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1 p-3 border-b border-background-200 bg-background-100">
      <button
        type="button"
        onClick={formatBold}
        title="Bold (Ctrl+B)"
        className="w-8 h-8 flex items-center justify-center rounded hover:bg-background-200 text-foreground-700 font-semibold cursor-pointer text-sm transition-colors"
      >
        B
      </button>
      <button
        type="button"
        onClick={formatItalic}
        title="Italic (Ctrl+I)"
        className="w-8 h-8 flex items-center justify-center rounded hover:bg-background-200 text-foreground-700 italic cursor-pointer text-sm transition-colors"
      >
        I
      </button>
      <button
        type="button"
        onClick={formatUnderline}
        title="Underline (Ctrl+U)"
        className="w-8 h-8 flex items-center justify-center rounded hover:bg-background-200 text-foreground-700 underline cursor-pointer text-sm transition-colors"
      >
        U
      </button>

      <span className="w-px h-5 bg-background-300 mx-1" />

      <button
        type="button"
        onClick={() => formatHeading("h2")}
        title="Heading 2"
        className="w-8 h-8 flex items-center justify-center rounded hover:bg-background-200 text-foreground-700 cursor-pointer text-xs font-semibold transition-colors"
      >
        H2
      </button>
      <button
        type="button"
        onClick={() => formatHeading("h3")}
        title="Heading 3"
        className="w-8 h-8 flex items-center justify-center rounded hover:bg-background-200 text-foreground-700 cursor-pointer text-xs font-medium transition-colors"
      >
        H3
      </button>

      <span className="w-px h-5 bg-background-300 mx-1" />

      <button
        type="button"
        onClick={formatBulletList}
        title="Bullet list"
        className="w-8 h-8 flex items-center justify-center rounded hover:bg-background-200 text-foreground-700 cursor-pointer text-sm transition-colors"
      >
        <i className="ri-list-unordered" />
      </button>
      <button
        type="button"
        onClick={formatOrderedList}
        title="Numbered list"
        className="w-8 h-8 flex items-center justify-center rounded hover:bg-background-200 text-foreground-700 cursor-pointer text-sm transition-colors"
      >
        <i className="ri-list-ordered" />
      </button>

      <span className="w-px h-5 bg-background-300 mx-1" />

      <input
        ref={linkUrlRef}
        type="text"
        placeholder="Paste link URL..."
        className="w-28 px-2 py-1 text-xs font-body border border-background-300 rounded bg-background-50 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
      />
      <button
        type="button"
        onClick={formatLink}
        title="Insert link"
        className="w-8 h-8 flex items-center justify-center rounded hover:bg-background-200 text-foreground-700 cursor-pointer text-sm transition-colors"
      >
        <i className="ri-link" />
      </button>

      <span className="w-px h-5 bg-background-300 mx-1" />

      <button
        type="button"
        onClick={onPasteWord}
        title="Paste from Word"
        className="flex items-center gap-1 px-2 py-1 text-xs font-body text-foreground-600 hover:text-foreground-900 hover:bg-background-200 rounded cursor-pointer transition-colors whitespace-nowrap"
      >
        <i className="ri-file-word-2-line text-sm" />
        Paste from Word
      </button>
    </div>
  );
}

/* ── ContentEditable with paste interception ── */
function EditorContentArea() {
  const [editor] = useLexicalComposerContext();

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const html = e.clipboardData.getData("text/html");
      if (html && (html.includes("mso-") || html.includes("WordDocument"))) {
        e.preventDefault();
        const text = e.clipboardData.getData("text/plain");
        if (text) {
          editor.update(() => {
            const root = $getRoot();
            root.clear();
            text.split("\n").forEach((line) => {
              const trimmed = line.trim();
              if (trimmed) {
                const p = $createParagraphNode();
                p.append($createTextNode(trimmed));
                root.append(p);
              }
            });
          });
        }
      }
    },
    [editor],
  );

  return (
    <div className="relative" onPaste={handlePaste}>
      <ContentEditable className="min-h-[320px] p-4 font-body text-sm text-foreground-800 focus:outline-none" />
    </div>
  );
}

/* ── Import initial HTML after first render ── */
function HtmlImporter({ html }: { html: string }) {
  const [editor] = useLexicalComposerContext();
  const didImport = useRef(false);

  useEffect(() => {
    if (didImport.current || !html || html === "<p></p>") return;
    didImport.current = true;

    const timer = setTimeout(() => {
      editor.update(() => {
        importHtmlToEditor(html);
      });
    }, 50);

    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

/* ── HTML → Lexical nodes ── */
function importHtmlToEditor(html: string) {
  const root = $getRoot();
  root.clear();

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const body = doc.body;

  const importNode = (node: Node): ReturnType<typeof $createTextNode> | null => {
    if (node.nodeType === 3) {
      const text = node.textContent || "";
      if (text.trim()) return $createTextNode(text);
      return null;
    }
    if (node.nodeType !== 1) return null;

    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();

    if (tag === "h2") {
      const h = $createHeadingNode("h2");
      el.childNodes.forEach((c) => {
        const n = importNode(c);
        if (n) h.append(n);
      });
      if (h.getChildrenSize() === 0) h.append($createTextNode(el.textContent || ""));
      return h;
    }
    if (tag === "h3") {
      const h = $createHeadingNode("h3");
      el.childNodes.forEach((c) => {
        const n = importNode(c);
        if (n) h.append(n);
      });
      if (h.getChildrenSize() === 0) h.append($createTextNode(el.textContent || ""));
      return h;
    }
    if (tag === "p") {
      const p = $createParagraphNode();
      el.childNodes.forEach((c) => {
        const n = importNode(c);
        if (n) p.append(n);
      });
      if (p.getChildrenSize() === 0 && el.textContent?.trim()) {
        p.append($createTextNode(el.textContent));
      }
      return p;
    }
    if (tag === "ul") {
      const list = $createListNode("bullet");
      el.childNodes.forEach((c) => {
        if ((c as HTMLElement).tagName?.toLowerCase() === "li") {
          const item = $createListItemNode();
          c.childNodes.forEach((gc) => {
            const n = importNode(gc);
            if (n) item.append(n);
          });
          if (item.getChildrenSize() === 0) item.append($createTextNode(c.textContent || ""));
          list.append(item);
        }
      });
      return list;
    }
    if (tag === "ol") {
      const list = $createListNode("number");
      el.childNodes.forEach((c) => {
        if ((c as HTMLElement).tagName?.toLowerCase() === "li") {
          const item = $createListItemNode();
          c.childNodes.forEach((gc) => {
            const n = importNode(gc);
            if (n) item.append(n);
          });
          if (item.getChildrenSize() === 0) item.append($createTextNode(c.textContent || ""));
          list.append(item);
        }
      });
      return list;
    }
    if (tag === "blockquote") {
      const q = $createQuoteNode();
      el.childNodes.forEach((c) => {
        const n = importNode(c);
        if (n) q.append(n);
      });
      if (q.getChildrenSize() === 0) q.append($createTextNode(el.textContent || ""));
      return q;
    }
    if (tag === "strong" || tag === "b") {
      const t = $createTextNode(el.textContent || "");
      t.setFormat("bold");
      return t;
    }
    if (tag === "em" || tag === "i") {
      const t = $createTextNode(el.textContent || "");
      t.setFormat("italic");
      return t;
    }

    return $createTextNode(el.textContent || "");
  };

  body.childNodes.forEach((child) => {
    const n = importNode(child);
    if (n) root.append(n);
  });

  if (root.getChildrenSize() === 0) {
    root.append($createParagraphNode());
  }
}

/* ── Plugin: fire onChange with HTML on every update ── */
function ChangeHtmlPlugin({ onChange }: { onChange: (html: string) => void }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }: { editorState: EditorState }) => {
      editorState.read(() => {
        const html = $generateHtmlFromNodes(editor);
        onChange(html);
      });
    });
  }, [editor, onChange]);

  return null;
}

/* ── Initial config ── */
const initialConfig = {
  namespace: "BlogEditor",
  theme,
  nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode, AutoLinkNode],
  onError: (error: Error) => {
    console.error("Lexical error:", error);
  },
};

/* ── Main wrapper ── */
export default function LexicalEditorWrapper({
  initialHtml,
  onChange,
  onEditorReady,
}: LexicalEditorWrapperProps) {
  const editorRef = useRef<LexicalEditor | null>(null);

  useEffect(() => {
    if (editorRef.current && onEditorReady) {
      onEditorReady(editorRef.current);
    }
  }, [onEditorReady]);

  const handlePasteWordDialog = () => {
    if (!editorRef.current) return;
    const text = prompt("Paste your Word document content as plain text:");
    if (text) {
      editorRef.current.update(() => {
        const root = $getRoot();
        root.clear();
        text.split("\n").forEach((line) => {
          const trimmed = line.trim();
          if (trimmed) {
            const p = $createParagraphNode();
            p.append($createTextNode(trimmed));
            root.append(p);
          }
        });
      });
    }
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className="border border-background-300 rounded-lg overflow-hidden bg-background-50">
        <ToolbarPlugin editorRef={editorRef} onPasteWord={handlePasteWordDialog} />
        <RichTextPlugin
          contentEditable={<EditorContentArea />}
          placeholder={
            <div className="absolute top-4 left-4 font-body text-sm text-foreground-400 pointer-events-none select-none">
              Start writing your blog post... Use the toolbar above to format your content.
            </div>
          }
          ErrorBoundary={() => (
            <div className="p-4 text-accent-600 font-body text-sm">
              Editor encountered an error. Please refresh.
            </div>
          )}
        />
      </div>
      <HistoryPlugin />
      <ChangeHtmlPlugin onChange={onChange} />
      {initialHtml ? <HtmlImporter html={initialHtml} /> : null}
    </LexicalComposer>
  );
}